import type { ProductSizeOption, ShippingFeeSettings } from '@repo/api-contracts';
import { checkDestinationTotalRules } from './destination-rules';
import { estimateShipping } from './estimate';
import {
  checkQuoteRequestRules,
  type QuoteRequestRuleContext,
  type QuoteRequestRuleErrors,
} from './quote-request-rules';
import {
  checkSizeAllocation,
  SIZE_TOTAL_EMPTY_MESSAGE,
  SIZE_TOTAL_INVALID_MESSAGE,
  type SizeFieldValues,
  type SizeQuantityRow,
  sizeTotalsFromFields,
} from './size-allocation';

interface QuoteRequestDestinationRuleValues {
  readonly desiredDate: string;
  readonly sizeFields: SizeFieldValues;
  readonly destinations: readonly {
    readonly key: string;
    readonly value: {
      readonly postalCode: string;
      readonly quantity: string;
      readonly sizes: readonly SizeQuantityRow[];
    };
  }[];
}

interface QuoteRequestDestinationRuleErrors {
  /** 최소 수량·금액·납기 규칙. 호출자의 스키마 칸 오류가 먼저다. */
  readonly totalErrors: QuoteRequestRuleErrors;
  /** 잘못되거나 빈 사이즈 총량은 스키마의 파생 quantity 오류보다 먼저다. */
  readonly sizeTotalError: string | null;
  /** 배송지 목록의 스키마 오류가 있으면 그 오류를 유지한다. */
  readonly allocationError: string | null;
  /** 카드 key → sizes 칸 오류. 그 카드의 스키마 sizes 오류가 먼저다. */
  readonly destinationSizeErrors: Readonly<Record<string, string>>;
}

/** 회원·비회원 공용 정책: 총량 규칙, 사이즈 배분, 빈 사이즈 카드(S9)의 보이는 오류. */
export const checkQuoteRequestDestinationRules = (
  values: QuoteRequestDestinationRuleValues,
  sizeOptions: readonly ProductSizeOption[],
  shippingFeeSettings: ShippingFeeSettings,
  context: QuoteRequestRuleContext,
): QuoteRequestDestinationRuleErrors => {
  if (sizeOptions.length === 0) {
    return {
      totalErrors: checkDestinationTotalRules(
        values.destinations,
        values.desiredDate,
        shippingFeeSettings,
        context,
      ),
      sizeTotalError: null,
      allocationError: null,
      destinationSizeErrors: {},
    };
  }

  const totals = sizeTotalsFromFields(sizeOptions, values.sizeFields);
  const totalErrors = checkQuoteRequestRules(
    {
      quantity: String(totals?.total ?? ''),
      desiredDate: values.desiredDate,
      shippingAmount: estimateShipping(
        values.destinations.map((item) => item.value.postalCode),
        shippingFeeSettings,
      ).shippingAmount,
    },
    context,
  );
  const sizeTotalError =
    totals === null
      ? SIZE_TOTAL_INVALID_MESSAGE
      : totals.total === 0
        ? SIZE_TOTAL_EMPTY_MESSAGE
        : null;
  const destinationSizeErrors: Record<string, string> = {};
  let allocationError: string | null = null;
  if (values.destinations.length >= 2) {
    if (totals !== null) {
      allocationError = checkSizeAllocation(
        sizeOptions,
        totals.bySize,
        values.destinations.map((item) => item.value),
      );
    }
    for (const item of values.destinations) {
      if (item.value.sizes.length === 0) {
        destinationSizeErrors[item.key] = '사이즈를 한 개 이상 넣어 주세요.';
      }
    }
  }
  return { totalErrors, sizeTotalError, allocationError, destinationSizeErrors };
};
